import type { Dispatch, SetStateAction } from 'react'
import type { I18nT } from '@runtime/core/i18n/i18n'

import { Text } from '@runtime/components/ui/Text'
import type { ProfileFullGame } from '@runtime/features/profile/model/profileTypes'
import styles from '@runtime/features/profile/history/review/HistoryReview.module.scss'
import HistoryReplayControls from '@runtime/features/profile/history/review/HistoryReplayControls'
import HistoryReviewBoard from '@runtime/features/profile/history/review/HistoryReviewBoard'
import HistoryReviewSummary from '@runtime/features/profile/history/review/HistoryReviewSummary'
import {
  buildHistoryReviewData,
  getPuzzleDotStyle,
} from '@runtime/features/profile/history/review/historyReview.utils'

type HistoryReviewProps = {
  t: I18nT
  selectedGame: ProfileFullGame | null
  reviewIndex: number
  reviewPlaying: boolean
  setReviewIndex: Dispatch<SetStateAction<number>>
  setReviewPlaying: Dispatch<SetStateAction<boolean>>
}

export default function HistoryReview({
  t,
  selectedGame,
  reviewIndex,
  reviewPlaying,
  setReviewIndex,
  setReviewPlaying,
}: HistoryReviewProps) {
  const {
    boardToShow,
    grid,
    isPuzzleGame,
    lastMove,
    meta,
    movesCount,
    p1Color,
    p1Name,
    p2Color,
    p2Name,
    puzzleClicks,
    puzzleTimeMs,
    score1,
    score2,
  } = buildHistoryReviewData(selectedGame, reviewIndex)
  const puzzleDotStyle = getPuzzleDotStyle()
  const reviewMode = typeof meta.mode === 'string' ? meta.mode : ''
  const puzzlePlayerName = p1Name || selectedGame?.myName || t('profile.guest')

  if (!boardToShow || !grid) {
    return (
      <div className={`${styles.loginReview} ${styles.loginReviewFull}`}>
        <div className={styles.loginSectionTitle}>
          {t('profile.review.title')}
        </div>

        <Text variant="legend" className={styles.loginReviewNotice}>
          {t('profile.review.boardNotAvailable')}
        </Text>
      </div>
    )
  }

  return (
    <div className={`${styles.loginReview} ${styles.loginReviewFull}`}>
      <div className={styles.loginSectionTitle}>
        {t('profile.review.title')}
      </div>

      <div className={styles.loginReviewGrid}>
        <div className={styles.reviewBoardWrap}>
          <HistoryReviewBoard
            boardToShow={boardToShow}
            grid={grid}
            lastMove={lastMove}
            mode={reviewMode}
            tiles={selectedGame?.tiles}
          />

          <HistoryReplayControls
            t={t}
            movesCount={movesCount}
            reviewIndex={reviewIndex}
            reviewPlaying={reviewPlaying}
            setReviewIndex={setReviewIndex}
            setReviewPlaying={setReviewPlaying}
          />
        </div>

        <HistoryReviewSummary
          t={t}
          movesCount={movesCount}
          isPuzzleGame={isPuzzleGame}
          p1Name={p1Name}
          p2Name={p2Name}
          p1Color={p1Color}
          p2Color={p2Color}
          score1={score1}
          score2={score2}
          puzzleClicks={puzzleClicks}
          puzzleTimeMs={puzzleTimeMs}
          puzzleDotStyle={puzzleDotStyle}
          puzzlePlayerName={puzzlePlayerName}
        />
      </div>
    </div>
  )
}
